import { eventBus, useOnline, useTranslation } from '@libs/core';
import { SystemEventTypeEnum } from '@libs/types';
import { useEffect, useRef } from 'react';
import { ToastBarType } from '../types/toastBar.type';
import { preloadIcon } from '../widgets/Image/imageUtils';

const DEFAULT_DELAY = 3000;
const OFFLINE_ICON = 'ic_alert_circle_small';

export const OFFLINE_MESSAGE_KEY = 'no.internet.connection.message';
export const OFFLINE_MESSAGE_FALLBACK = 'You’re offline — some actions might be unavailable.';

/**
 * Builds the "you are offline" toast. Shared with the retry button of the offline popup
 * (see WidgetWrapper), so both places show the same message, icon and duration.
 */
export const buildOfflineToast = (message: string): ToastBarType => ({ message, iconName: OFFLINE_ICON, delay: DEFAULT_DELAY });

export const Offline = () => {
    const isOnline = useOnline();
    const { t } = useTranslation();
    const userLostInternetConnection = useRef<boolean>(false);

    // The offline toast icon can no longer be downloaded once the connection is lost, so cache it while still online.
    // Runs again on every transition back online, which covers an app started offline (first attempt failed).
    useEffect(() => {
        if (isOnline) {
            preloadIcon(OFFLINE_ICON);
        }
    }, [isOnline]);

    useEffect(() => {
        let toastMessage: ToastBarType | undefined;
        if (!isOnline) {
            userLostInternetConnection.current = true;
            toastMessage = buildOfflineToast(t(OFFLINE_MESSAGE_KEY, OFFLINE_MESSAGE_FALLBACK));
        } else if (userLostInternetConnection.current) {
            userLostInternetConnection.current = false;
            toastMessage = {
                message: t('internet.connection.restored.message', 'You’re online again!'),
                iconName: 'ic_check_20',
                delay: DEFAULT_DELAY,
            };
        }
        if (toastMessage) {
            eventBus.emitGeneral(SystemEventTypeEnum.TOAST_MESSAGE, toastMessage);
        }
    }, [isOnline, t]);

    return false;
};
